import type { LmProvider } from '@/01-models/lm';
import type { Endpoint, EndpointType, LlamaCppServerTimingsMode } from '@/01-models/types';
import type { FakeLmDebugModeStatus } from '@/features/fake-lm/runtime/fakeLmDebugMode';
import { createLmFetch } from '@/features/lm/fetchFactory';
import { createModuleLoader } from '@/utils/module-loader';

const openAiProviderModuleLoader = createModuleLoader({
  importModule: () => import('@/features/lm/openai'),
  onPrefetchError: ({ error }) => {
    console.error('Failed to prefetch OpenAI provider:', error);
  },
});

const ollamaProviderModuleLoader = createModuleLoader({
  importModule: () => import('@/features/lm/ollama'),
  onPrefetchError: ({ error }) => {
    console.error('Failed to prefetch Ollama provider:', error);
  },
});

const transformersJsProviderModuleLoader = createModuleLoader({
  importModule: () => import('@/features/transformers-js/provider'),
  onPrefetchError: ({ error }) => {
    console.error('Failed to prefetch Transformers.js provider:', error);
  },
});

const llamaCppProviderModuleLoader = createModuleLoader({
  importModule: () => import('@/features/llama-cpp-browser/provider'),
  onPrefetchError: () => {
    console.warn('[llama-cpp-browser] provider-prefetch-failed');
  },
});

const promptApiProviderModuleLoader = createModuleLoader({
  importModule: () => import('@/features/prompt-api/provider'),
  onPrefetchError: ({ error }) => {
    console.error('Failed to prefetch Prompt API provider:', error);
  },
});

const naidanRpcProviderModuleLoader = createModuleLoader({
  importModule: () => import('@/features/naidan-rpc-integration/adapters/lm-provider'),
  onPrefetchError: () => console.warn('[naidan-rpc] provider-prefetch-failed'),
});

export async function loadLmProvider({ endpoint, fakeLmDebugModeStatus, llamaCppServerTimings }: {
  endpoint: Endpoint,
  llamaCppServerTimings?: LlamaCppServerTimingsMode,
  fakeLmDebugModeStatus: FakeLmDebugModeStatus,
}): Promise<LmProvider> {
  switch (endpoint.type) {
  case 'naidan_rpc': {
    const { NaidanRpcLmProvider } = await naidanRpcProviderModuleLoader.load();
    return new NaidanRpcLmProvider({ registrationId: endpoint.registrationId });
  }
  case 'openai': {
    const { OpenAIProvider } = await openAiProviderModuleLoader.load();
    return new OpenAIProvider({
      llamaCppServerTimings,
      endpoint: endpoint.url,
      headers: cloneEndpointHttpHeaders({ headers: endpoint.httpHeaders }),
      fetcher: createLmFetch({ endpointUrl: endpoint.url, fakeLmDebugModeStatus }),
    });
  }
  case 'ollama': {
    const { OllamaProvider } = await ollamaProviderModuleLoader.load();
    return new OllamaProvider({
      endpoint: endpoint.url ?? '',
      headers: cloneEndpointHttpHeaders({ headers: endpoint.httpHeaders }),
      fetcher: createLmFetch({ endpointUrl: endpoint.url, fakeLmDebugModeStatus }),
    });
  }
  case 'transformers_js': {
    const { TransformersJsProvider } = await transformersJsProviderModuleLoader.load();
    return new TransformersJsProvider();
  }
  case 'llama_cpp_browser': {
    const { LlamaCppBrowserProvider } = await llamaCppProviderModuleLoader.load();
    return new LlamaCppBrowserProvider();
  }
  case 'browser_provided_lm': {
    const { PromptApiProvider } = await promptApiProviderModuleLoader.load();
    return new PromptApiProvider();
  }
  case 'unsupported_experimental_endpoint':
    throw new Error(`Unsupported experimental endpoint: ${String(endpoint.persistedType)}`);
  default: {
    const _ex: never = endpoint;
    throw new Error(`Unhandled endpoint: ${String(_ex)}`);
  }
  }
}

export async function prefetchLmProvider({ endpointType }: {
  endpointType: EndpointType,
}): Promise<void> {
  switch (endpointType) {
  case 'naidan_rpc':
    await naidanRpcProviderModuleLoader.prefetch();
    break;
  case 'openai':
    await openAiProviderModuleLoader.prefetch();
    break;
  case 'ollama':
    await ollamaProviderModuleLoader.prefetch();
    break;
  case 'transformers_js':
    await transformersJsProviderModuleLoader.prefetch();
    break;
  case 'llama_cpp_browser':
    await llamaCppProviderModuleLoader.prefetch();
    break;
  case 'browser_provided_lm':
    await promptApiProviderModuleLoader.prefetch();
    break;
  default: {
    const _ex: never = endpointType;
    throw new Error(`Unhandled endpoint type: ${_ex}`);
  }
  }
}

export function cloneEndpointHttpHeaders({ headers }: {
  headers: [string, string][] | undefined,
}): [string, string][] | undefined {
  return headers === undefined
    ? undefined
    : headers.map(([name, value]) => [name, value]);
}

// Export internal state and logic used only for testing here. Do not reference these in production logic.
// ESLint-required for TypeScript modules.
export const TEST_ONLY = {
};
